/**
 * src/modules/orders-list/cards.ts — Faza 6.2.24 (2026-05-03) split.
 *
 * Cards view (alternatywa do tabeli) — renderuje karty zamówień grupowane po dacie
 * (dziś/wczoraj/tydzień/starsze) z pełnym filtrowaniem, paginacją, status dropdown,
 * assign-product buttons (Allegro offer drawer / PS focused mpo-link-drawer).
 *
 * Wycięte z `src/modules/orders-list.ts` (1651 LOC) — patrz
 * `.plans/audit-orders-list-split.md`.
 */

import type { OrderItem, OrderStatusDef } from '../../types/dto.ts';

// FIX (Faza E.1): proper ESM imports z mpo.js i packing.js (oba CORE — w main.js).
import { _openMpoLinkDrawer } from '../mpo.js';
import { $ordersHydrated } from '../../stores/orders.js';
import { todayLocalISO, localDateNDaysAgo, formatLocalDate, formatLocalDateTime } from '../_datetime.ts';
import { orderUrl } from '../order-ref.ts';
import { zestawAllegroChip } from '../zestaw-allegro.ts';
import { lokalizacjaBadgeHtml } from '../orders-shared.ts'; // 21.10.2026: lokalizacja edytowalna w miejscu
import { statusFilterMatches, _prodZnacznikHtml, formatujTelefon, isOrdersGlobalSearchActive, orderSearchGroupContextHtml, roznicaKodow, roznyKod, kodDiffTitle, KOD_DIFF_STYLE } from '../orders-shared.ts';
import { kodyPozycjiAllegro, sygnaturaDiffTitle, sygnaturaZakupuTitle } from '../orders-shared.ts'; // 2026-10-02: EAN · SKU · sygnatura przy pozycji Allegro
import { morfuj } from '../plynne.ts';
import { chipZlecenHtml } from './chip-zlecenia.ts'; // 2026-10-08: kto jest przypisany (otwarte zlecenia)

var state: any = (typeof window !== 'undefined' && window.__sbState) || (function () {
  var _p = new Proxy({}, {
    get: function (_, k: any) { return window.__sbState ? (window.__sbState as any)[k] : undefined; },
    set: function (_, k: any, v: any) { if (window.__sbState) (window.__sbState as any)[k] = v; return true; },
    has: function (_, k: any) { return window.__sbState ? (k in (window.__sbState as any)) : false; },
  });
  return _p;
})();

// Lazy delegates (cross-module)
function _buildPaymentBadge(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports._buildPaymentBadge) || window._buildPaymentBadge) as any).apply(this, args); }
function _buildShipmentBadge(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports._buildShipmentBadge) || window._buildShipmentBadge) as any).apply(this, args); }
function _lazyRefreshVisiblePSOrders(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports._lazyRefreshVisiblePSOrders) || (window as any)._lazyRefreshVisiblePSOrders) as any).apply(this, args); }
function _updateBulkBar(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports._updateBulkBar) || (window as any)._updateBulkBar) as any).apply(this, args); }
function getOrderApiId(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports.getOrderApiId) || window.getOrderApiId) as any).apply(this, args); }
function orderStatusOptionsHtml(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports.orderStatusOptionsHtml) || (window as any).orderStatusOptionsHtml) as any).apply(this, args); }
// S-139 (07.10.2026): punktowa podmiana badge'a statusu na liście (orders/drawer.ts) zamiast renderOrdersView().
function _patchOrderStatusInList(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports._patchOrderStatusInList) || (window as any)._patchOrderStatusInList) as any).apply(this, args); }
function renderPagination(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports.renderPagination) || window.renderPagination) as any).apply(this, args); }
function showOrderDetail(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports.showOrderDetail) || window.showOrderDetail) as any).apply(this, args); }
function statusColorForLabel(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports.statusColorForLabel) || window.statusColorForLabel) as any).apply(this, args); }
function upsertOrderInStore(this: any, ...args: any[]) { return (((window.__sbExports && window.__sbExports.upsertOrderInStore) || window.upsertOrderInStore) as any).apply(this, args); }

/* -----------------------------------------------
     sb:offer-link:changed → reload orders bez F5
     2026-05-27: po "Przypisz produkt" w drawerze chcemy żeby lista zamówień
     odświeżyła kafelek (Brak → SB tile) bez konieczności odświeżenia strony.
     ----------------------------------------------- */
if (typeof window !== 'undefined' && !(window as any)._sbOfferLinkChangedBound) {
  (window as any)._sbOfferLinkChangedBound = true;
  window.addEventListener('sb:offer-link:changed', function(e: Event) {
    try {
      // S-139 przegląd: szuflada zamówienia (orders/drawer.ts) cache'uje `order._productLinks`
      // i pomija dociągnięcie, gdy KAŻDA pozycja już ma wpis — ten cache żyje na obiekcie
      // zamówienia w magazynie, więc przeżywa też pominięty refetch listy (inny widok niż
      // Zamówienia, cache stron wyżej). Bez skasowania go tutaj, zapis w szufladzie po
      // przepięciu/usunięciu powiązania rysowałby stary kafel produktu do najbliższego F5.
      try {
        var detail: any = (e as CustomEvent).detail || {};
        var zmienionaOferta = detail.offer_id != null ? String(detail.offer_id) : '';
        var hurtowo = !!detail.hurt;
        var listaZamowien: any[] = ((window as any).SellBaseData && (window as any).SellBaseData.orders) || [];
        listaZamowien.forEach(function(o: any) {
          if (!o || !o._productLinks) return;
          if (hurtowo || !zmienionaOferta) { delete o._productLinks; return; }
          if (Object.prototype.hasOwnProperty.call(o._productLinks, zmienionaOferta)) delete o._productLinks[zmienionaOferta];
        });
      } catch (_e) { /* magazyn dogoni przy najbliższym otwarciu szuflady */ }
      // Bust sessionStorage __sb_orders_pg:* cache — inaczej `loadAllegroOrdersIntoPanel(true)`
      // pobierze stary wynik (TTL 60s) bez świeżego sellbase_products dla itemu.
      try {
        var toRemove: string[] = [];
        for (var i = 0; i < sessionStorage.length; i++) {
          var k = sessionStorage.key(i);
          if (k && (k.indexOf('__sb_orders_pg:') === 0 || k.indexOf('__sb_orders_search:') === 0)) toRemove.push(k);
        }
        toRemove.forEach(function(k) { sessionStorage.removeItem(k); });
      } catch (_) {}
      // S-139 (audyt 07.10): poza widokiem Zamówień render i tak jest pomijany niżej —
      // ale ciężki GET unified (loadAllegroOrdersIntoPanel) leciał za każdym razem, nawet
      // z Katalogu. Cache stron jest już wyczyszczony (wyżej), więc powrót na Zamówienia
      // dociągnie świeże dane sam — tu wystarczy pominąć fetch, gdy user jest gdzie indziej.
      if ((state as any).currentView !== 'orders') return;
      var fn = (window as any).loadAllegroOrdersIntoPanel;
      if (typeof fn === 'function') {
        fn(true);
      } else if (typeof (window as any).renderOrdersView === 'function') {
        (window as any).renderOrdersView();
      }
    } catch (_) {}
  });
}

/* -----------------------------------------------
     renderOrderCards — cards view
     ----------------------------------------------- */
export function renderOrderCards() {
    var listContainer = document.getElementById('orders-list-container');
    if (!listContainer) return;

    var orders = SellBaseData.orders || [];

    // Apply filters
    var searchEl = document.getElementById('order-search');
    var searchQuery = searchEl ? (searchEl as any).value.trim().toLowerCase() : '';
    var filtered = orders.filter(function(o: any) {
      // Status filter
      if (!isOrdersGlobalSearchActive() &&
          (!Array.isArray((state as any).ordersFilterStatuses) || !(state as any).ordersFilterStatuses.length) &&
          (state as any).ordersStatusFilter !== 'all') {
        // ETAP 3: porównujemy KLUCZE. Nazwa jest zmienialna, więc porównanie po
        // niej gubiło zamówienia zaraz po przemianowaniu statusu.
        // #7 (2026-08-13): filtr bywa zlepkiem kluczy (klik w grupę) — matcher
        // wspólny, bo równość wycinała 100% wyników grup wielostatusowych.
        var sk = o.status ? (o.status.key || '') : '';
        if (!statusFilterMatches(sk, (state as any).ordersStatusFilter)) return false;
      }
      // Source filter
      if ((!Array.isArray((state as any).ordersFilterSources) || !(state as any).ordersFilterSources.length) &&
          (state as any).ordersSourceFilter !== 'all') {
        var src = o.source ? (o.source.key || '') : '';
        if (src !== (state as any).ordersSourceFilter) return false;
      }
      // Payment filter — 2026-06-09: status płatności idzie SERVER-SIDE (param payment_status,
      // patrz auth-sync.ts). Client-side zostaje TYLKO 'cod' (metoda płatności, nie status;
      // unified/listing nie ma filtra po metodzie — stats liczy cod_count osobno).
      // 2026-09-03 (audyt): 'cod' idzie server-side (payment_dims=cod,cod_paid w auth-sync.ts) —
      // filtr po stronie klienta ciął tylko bieżącą stronę i rozjeżdżał się z licznikiem.
      // Shipment filter — 2026-06-09: SERVER-SIDE (param shipment_status). Brak filtra client-side.
      // Search
      if (searchQuery && String((state as any).ordersFilterQuery || '').trim().length < 2 && !(state as any).ordersSearchActive) {
        var productsText = Array.isArray(o.items) ? o.items.map(function(it: OrderItem) { return it.name || ''; }).join(' ') : '';
        var haystack = (
          o.id + ' ' +
          (o.customer || '') + ' ' +
          (o.buyer_login || '') + ' ' +
          (o.customerEmail || o.email || '') + ' ' +
          (o.customerPhone || o.phone || '') + ' ' +
          (o.buyerCompanyName || '') + ' ' +
          (o.buyerNip || '') + ' ' +
          (o.source_order_id || o.allegro_id || '') + ' ' +
          (o.orderNumber || '') + ' ' +
          productsText
        );
        // deburrText (0180): fold diakrytyków po obu stronach lokalnego filtra
        var _deburr = (window as any).deburrText || function(s: any) { return String(s || '').toLowerCase(); };
        if (_deburr(haystack).indexOf(_deburr(searchQuery)) === -1) return false;
      }
      return true;
    });

    // Sorting
    var sortSel = document.getElementById('order-sort-select');
    var sortVal = String((state as any).ordersSortMode || (sortSel ? (sortSel as any).value : '') || 'date_desc');
    filtered = filtered.slice().sort(function(a, b) {
      if (sortVal === 'date_desc') return (b.date || '') > (a.date || '') ? 1 : -1;
      if (sortVal === 'date_asc') return (a.date || '') > (b.date || '') ? 1 : -1;
      if (sortVal === 'amount_desc') return (b.amount || 0) - (a.amount || 0);
      if (sortVal === 'amount_asc') return (a.amount || 0) - (b.amount || 0);
      return 0;
    });

    // Sprint 25 (2026-05-03): server-side pagination — SellBaseData.orders ma tylko bieżącą stronę.
    var page = (state as any).currentPage.orders || 1;
    var perPage = (state as any).itemsPerPage || 20;
    var totalItems = (state as any).ordersTotalCount != null ? (state as any).ordersTotalCount : filtered.length;
    var totalPages = (state as any).ordersTotalPages || Math.ceil(totalItems / perPage) || 1;
    if (page > totalPages && totalPages > 0) page = totalPages;
    var paginated = filtered;  // bez slice — server zwraca już posortowaną stronę

    // Group by date — Sprint 25 (2026-05-05): lokalna strefa (Europe/Warsaw), nie UTC.
    var today = todayLocalISO();
    var yesterday = localDateNDaysAgo(1);
    var weekAgo = localDateNDaysAgo(7);

    var groups: any[] = [
      { key: 'today', label: 'Dzisiaj', orders: [] },
      { key: 'yesterday', label: 'Wczoraj', orders: [] },
      { key: 'week', label: 'Ten tydzień', orders: [] },
      { key: 'older', label: 'Starsze', orders: [] }
    ];

    paginated.forEach(function(o: any) {
      // Sprint 25 (2026-05-05): backend zwraca order.date jako pełny ISO (UTC) lub date-only.
      // Konwertuj do lokalnej daty żeby grupowanie działało zgodnie ze strefą czasową.
      var rawD = o.date || '';
      var d = rawD && rawD.length > 10 ? formatLocalDate(rawD) : String(rawD).substring(0, 10);
      if (d === today) groups[0].orders.push(o);
      else if (d === yesterday) groups[1].orders.push(o);
      else if (d > weekAgo) groups[2].orders.push(o);
      else groups[3].orders.push(o);
    });

    var html = '<div class="orders-cards-grid">';
    var hasAny = false;

    // Nagłówki „Dzisiaj / Wczoraj / Ten tydzień / Starsze" mają sens WYŁĄCZNIE przy
    // domyślnej kolejności (najnowsze u góry). Przy każdej innej kłamały o kolejności:
    // „najstarsze" pokazywało najpierw najstarsze DZISIAJ, potem z tego tygodnia itd.
    // (zgłoszenie usera 2026-08-21). Poza domyślnym sortem lecimy jedną płaską listą.
    if (sortVal === 'date_desc') {
      groups.forEach(function(g) {
        if (!g.orders.length) return;
        hasAny = true;
        html += '<div class="orders-date-group">' + escapeHtml(g.label) + '</div>';
        g.orders.forEach(function(order: any) {
          html += _buildOrderCardHTML(order);
        });
      });
    } else {
      paginated.forEach(function(order: any) {
        hasAny = true;
        html += _buildOrderCardHTML(order);
      });
    }

    if (!hasAny) {
      // Sprint 25 fix (2026-05-04): przed pierwszym fetch-em (lub trakcie ładowania)
      // pokazujemy stan "Ładowanie…" zamiast "Brak zamówień". Bez tego user widział
      // mylący komunikat przez 200-500ms zanim `loadAllegroOrdersIntoPanel()` zwrócił
      // pierwszą paczkę. `$ordersHydrated` = false → fetch w toku albo jeszcze nie zaczęty.
      var _hydrated = false;
      try { _hydrated = !!$ordersHydrated.get(); } catch (_) { _hydrated = !!(state as any).ordersHydrated; }
      if (!_hydrated || (state as any).ordersHydrating) {
        html += '<div style="padding:40px;text-align:center;color:var(--color-text-muted);grid-column:1/-1;display:flex;align-items:center;justify-content:center;gap:8px">' +
          '<i data-lucide="loader-2" style="width:18px;height:18px;animation:sb-spin 1s linear infinite"></i>' +
          '<span>Ładowanie zamówień…</span>' +
        '</div>';
        // Wstrzyknij keyframes raz (idempotent)
        if (!document.getElementById('sb-spin-keyframes')) {
          var st = document.createElement('style');
          st.id = 'sb-spin-keyframes';
          st.textContent = '@keyframes sb-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}';
          document.head.appendChild(st);
        }
      } else {
        html += '<div style="padding:40px;text-align:center;color:var(--color-text-muted);grid-column:1/-1">Brak zamówień spełniających kryteria.</div>';
      }
    }

    html += '</div>';
    // S-139 (właściciel 07.10: „płynnie, bez odświeżania całej strony”): lista wchodzi przez morfowanie po data-order-id —
    // karta bez zmian zostaje TYM SAMYM węzłem (otwarte menu statusu, zaznaczenie, hover, scroll), zmieniona albo nowa
    // wchodzi w całości i dostaje zdarzenia niżej (strażnik __sbOcBound). Dotyczy zapisów, auto-odświeżania i powrotów.
    morfuj(listContainer, html, { klucz: 'data-order-id' });
    // ETAP 4: przeciagniecie tagu z panelu na karte zamowienia (patrz virtual.ts).
    try { (window as any).__sbOrderTags && (window as any).__sbOrderTags.uzbrojListeNaUpuszczanie(listContainer); } catch (_e) { /* lista dziala dalej */ }
    // Faza 62 cd.: scoped do listContainer — globalne lucide.createIcons() na 1000+ kartach × wiele ikon = wolne.
    if (typeof lucide !== 'undefined') { try { lucide.createIcons({ nodes: [listContainer as HTMLElement] }); } catch (_: any) {} }

    // Bind card events
    listContainer.querySelectorAll('.order-card').forEach(function(card) {
      // S-139: karta zachowana przez morfowanie ma już swoje zdarzenia — bez dubli; ZAZNACZENIE zawsze z ordersSelectedIds
      // (przegląd: po „Odznacz”/„Połącz w grupę” zachowana karta zostawała wizualnie zaznaczona przy pustym zbiorze)
      if ((card as any).__sbOcBound) {
        var _zazn = (state as any).ordersSelectedIds.has((card as any).dataset.orderId);
        var _cbZ = card.querySelector('.order-card-check') as HTMLInputElement | null;
        if (_cbZ && _cbZ.checked !== _zazn) _cbZ.checked = _zazn;
        card.classList.toggle('selected', _zazn);
        return;
      }
      (card as any).__sbOcBound = true;
      var orderId = (card as any).dataset.orderId;

      // Cały card klikalny → otwiera drawer (z wyjątkiem elementów interaktywnych)
      card.addEventListener('click', function(this: any, e: any) {
        // 2026-09-08 (prośba właściciela): Ctrl/⌘/Shift+klik = nowa karta. W link numeru (#…) robi to
        // przeglądarka; poza nim otwieramy sami. Zwykły klik w numer = drawer jak reszta karty.
        var _mod = !!(e.ctrlKey || e.metaKey || e.shiftKey);
        if (e.target!.closest('.zk-head-id')) { if (_mod) return; e.preventDefault(); showOrderDetail(orderId); return; }
        if (e.target!.closest('a, button, input, select, .oc-status-btn-wrap, .assign-product-btn')) return;
        if (_mod) {
          var _adresMod = String(this.dataset.orderUrl || '');
          if (_adresMod) { try { window.open(_adresMod, '_blank', 'noopener'); } catch (_eo) { /* nowa karta to udogodnienie */ } }
          return;
        }
        // 2026-07-29: klik w przyciętą wiadomość/notatkę rozwija ją NA MIEJSCU
        // zamiast otwierać drawer (długie prośby o fakturę nie mieszczą się w 2 liniach).
        var _noteRow = e.target!.closest('.oc-note-row');
        if (_noteRow) {
          e.stopPropagation();
          _noteRow.classList.toggle('is-expanded');
          return;
        }
        // S-160 (MOB-2): na telefonie szuflada pojawia się po 2–3 s (doładowanie modułu + dane) — karta od razu
        // pokazuje, że dotknięcie zadziałało (mobile.css .zk-otwieranie), do otwarcia szuflady albo 8 s.
        var _karta = this as HTMLElement;
        _karta.classList.add('zk-otwieranie');
        var _krok = 0;
        var _czek = setInterval(function() {
          var _sz = document.getElementById('order-drawer');
          if ((_sz && _sz.classList.contains('open')) || ++_krok > 53) { clearInterval(_czek); _karta.classList.remove('zk-otwieranie'); }
        }, 150);
        showOrderDetail(orderId);
      });
      (card as any).style.cursor = 'pointer';
      // 2026-09-08: środkowy przycisk myszy na karcie (poza linkami/przyciskami) = nowa karta z zamówieniem.
      card.addEventListener('auxclick', function(this: any, e: any) {
        if (!e || e.button !== 1) return;
        if (e.target && e.target.closest && e.target.closest('a, button, input, select, .oc-status-btn-wrap, .assign-product-btn')) return;
        var _adresAux = String(this.dataset.orderUrl || '');
        if (!_adresAux) return;
        e.preventDefault();
        try { window.open(_adresAux, '_blank', 'noopener'); } catch (_ea) { /* nowa karta to udogodnienie */ }
      });

      // Checkbox
      var cb = card.querySelector('.order-card-check');
      if (cb) {
        cb.addEventListener('change', function(this: any, e: any) {
          e.stopPropagation();
          if (this.checked) (state as any).ordersSelectedIds.add(orderId);
          else (state as any).ordersSelectedIds.delete(orderId);
          card.classList.toggle('selected', this.checked);
          _updateBulkBar();
        });
        if ((state as any).ordersSelectedIds.has(orderId)) { (cb as any).checked = true; card.classList.add('selected'); }
      }

      // Status dropdown button
      var statusBtn = card.querySelector('.oc-status-btn');
      if (statusBtn) {
        statusBtn.addEventListener('click', function(this: any, e: any) {
          e.stopPropagation();
          // Zamknij inne otwarte dropdowny
          document.querySelectorAll('.oc-status-dropdown.open').forEach(function(d) {
            if (d !== wrap) { d.classList.remove('open'); (d as any).style.display = 'none'; }
          });
          var wrap = (statusBtn!.parentNode as any).querySelector('.oc-status-dropdown');
          if (!wrap) return;
          var isOpen = wrap.classList.contains('open');
          if (isOpen) { wrap.classList.remove('open'); (wrap as any).style.display = 'none'; return; }
          // Buduj dropdown z opcjami statusów
          var order = SellBaseData.getOrderById(orderId);
          var currentLabel = order ? (order.status ? order.status.label : '') : '';
          var allStatuses = SellBaseData.orderStatuses.slice().sort(function(a: OrderStatusDef, b: OrderStatusDef){return (a.sort_order || 0) - (b.sort_order || 0);}).map(function(s: OrderStatusDef){return {label:s.name,color:statusColorForLabel(s.name)};});
          var items = allStatuses.map(function(s: { label: string; color: string }) {
            var isCurrent = s.label === currentLabel;
            return '<button class="oc-status-option ' + (isCurrent ? 'current' : '') + '" data-status="' + escapeAttr(s.label) + '">' +
              '<span class="status-badge ' + (s.color || 'info') + '" style="font-size:10px;pointer-events:none">' + escapeHtml(s.label) + '</span>' +
            '</button>';
          }).join('');
          wrap.innerHTML = '<div class="oc-status-dropdown-inner">' + items + '</div>';
          (wrap as any).style.display = 'block';
          wrap.classList.add('open');
          lucide.createIcons();
          // Bind opcji
          wrap.querySelectorAll('.oc-status-option').forEach(function(opt: any) {
            opt.addEventListener('click', function(this: any, ev: any) {
              ev.stopPropagation();
              var newStatus = this.dataset.status;
              wrap!.classList.remove('open'); (wrap! as any).style.display = 'none';
              var order = SellBaseData.getOrderById(orderId);
              if (!order) return;
              var apiId = getOrderApiId(order);
              if (!apiId) { showToast('Brak ID zamówienia', 'error'); return; }
              (statusBtn! as any).disabled = true;
              SellBaseAPI.updateOrderStatus(apiId, newStatus).then(function() {
                // S-139 (audyt 07.10, błąd danych): KLUCZ z kluczStatusu, nie slug nazwy — slug
                // ("w_realizacji") nie pasował do klucza z drzewka ("processing"), więc karta po
                // zmianie zostawała w złej grupie filtra do najbliższego pełnego odświeżenia.
                var _kl: any = (window as any).__sbExports && (window as any).__sbExports.kluczStatusu;
                var _statusKey = _kl ? String(_kl(newStatus) || '') : newStatus.toLowerCase().replace(/\s+/g, '_');
                order.status = { key: _statusKey, label: newStatus, color: statusColorForLabel(newStatus) };
                upsertOrderInStore(order);
                // Sprint 18.5: cross-tab broadcast
                try { (window as any).__sbBroadcast?.broadcast('order:updated', { id: apiId, status: newStatus }); } catch (_) {}
                (statusBtn! as any).disabled = false;
                // Liczniki w pasku mają cache po obu stronach — bez wymuszenia grupa
                // potrafiła twierdzić, że coś w niej jest, choć lista była już pusta.
                try {
                  var _st: any = (window as any).__sbOdswiezStatsZamowien;
                  if (typeof _st === 'function') _st(true);
                } catch (_eSt) { /* liczniki dogonią przy następnym odświeżeniu */ }
                try { var _inv: any = (window as any)._invalidateOrdersPageCache; if (typeof _inv === 'function') _inv(); } catch (_eInv) {}
                // S-139: punktowa zmiana zamiast renderOrdersView() — patchuje tylko tę kartę
                // (albo ją wygasza, gdy nowy status wypadł z filtra paska), reszta listy nie mruga.
                _patchOrderStatusInList(order.id, order.status);
                showToast('Status zmieniony', 'success');
              }).catch(function(err: any) {
                (statusBtn! as any).disabled = false;
                showToast('Błąd: ' + (err.message || ''), 'error');
              });
            });
          });
          // Zamknij przy kliknięciu poza
          setTimeout(function() {
            document.addEventListener('click', function _closer() {
              wrap!.classList.remove('open'); (wrap! as any).style.display = 'none';
              document.removeEventListener('click', _closer);
            });
          }, 0);
        });
      }

      // Assign product buttons
      card.querySelectorAll('.assign-product-btn').forEach(function(btn) {
        btn.addEventListener('click', async function(this: any, e: any) {
          e.stopPropagation();
          var itemId = this.dataset.itemId;
          var order = SellBaseData.getOrderById(orderId);
          if (!order) return;
          var items = Array.isArray(order.items) ? order.items : [];
          var item = items.find(function(it: OrderItem) { return String(it.id || it.line_item_id || '') === String(itemId); });
          if (!item) { showToast('Nie znaleziono pozycji zamówienia', 'error'); return; }
          // 2026-08-28 (prośba usera): JEDEN modal przypisania dla wszystkich
          // źródeł (z propozycjami automatycznymi) — Allegro szło dotąd do
          // drawera oferty z małym polem szukania. Router żyje w chunku mpo.
          var w: any = window as any;
          var fn = w.przypiszProduktDoPozycji;
          if (typeof fn !== 'function') {
            try { await import('../mpo.js'); } catch (e2: any) {
              showToast('Nie udało się załadować modułu powiązań: ' + ((e2 && e2.message) || ''), 'error');
              return;
            }
            fn = w.przypiszProduktDoPozycji;
          }
          if (typeof fn === 'function') fn(order, item);
          else showToast('Modal przypisania niedostępny', 'error');
        });
      });
    });

    renderPagination('orders-pagination', page, totalPages, totalItems, 'orders');
    _updateBulkBar();
    // Lazy background refresh dla widocznych PS orderów z pustym adresem
    setTimeout(function() { _lazyRefreshVisiblePSOrders(paginated); }, 800);

    // Faza 5 (internal_tasks): chip „👤 <imię>" na kartach z otwartym przypisaniem pracownika.
    // Best-effort, async, po pełnym renderze. Cards view re-renderuje innerHTML w całości
    // przy każdym renderOrderCards() (nie jest wirtualizowany — wirtualizacja dotyczy tylko
    // table/row view w virtual.ts), więc ten hook wystarczy — odpali się po każdym re-renderze.
    try { _decorateCardsWithInternalTasks(listContainer); } catch (_: any) { /* niekrytyczne */ }
}

/* -----------------------------------------------
     _decorateCardsWithInternalTasks — chip otwartego przypisania pracownika
     na kartach zamówień. Best-effort: try/catch, async, brak/błąd nie psuje listy.
     ----------------------------------------------- */
function _itEntityTypeForOrder(order: any): string {
  // Identyczna logika jak w order-drawer-core.ts (drawer „Przypisz pracownika"),
  // żeby entity_type kart == entity_type którego używa drawer do przypisania.
  var srcKey = (order && order.source && typeof order.source === 'object')
    ? String(order.source.key || '').toLowerCase()
    : String((order && order.source) || '').toLowerCase();
  if (order && (order.is_allegro || srcKey === 'allegro')) return 'order_allegro';
  if (order && (order.is_erli || srcKey === 'erli')) return 'order_erli';
  if (order && (order.is_ebay || srcKey === 'ebay')) return 'order_ebay';
  if (order && (order.is_prestashop || srcKey === 'prestashop' || srcKey === 'ps')) return 'order_ps';
  return 'order_manual';
}

var _itDekoratorPokolenie = 0;  // S-139 przegląd 2: starsza odpowiedź nie nadpisuje nowszej
async function _decorateCardsWithInternalTasks(container: HTMLElement) {
  if (!container) return;
  var pokolenie = ++_itDekoratorPokolenie;
  // 1) Zbierz widoczne karty + (entity_type, entity_id). entity_id = data-order-id,
  //    czyli ten sam order.id którego używa drawer przy tworzeniu przypisania.
  var cards = Array.prototype.slice.call(container.querySelectorAll('.order-card')) as HTMLElement[];
  if (!cards.length) return;

  var pairs: [string, string][] = [];
  var byKey: Record<string, HTMLElement[]> = {};
  var seen: Record<string, boolean> = {};
  cards.forEach(function(card) {
    var oid = (card as any).dataset ? (card as any).dataset.orderId : '';
    if (!oid) return;
    var order = (typeof SellBaseData !== 'undefined' && SellBaseData.getOrderById)
      ? SellBaseData.getOrderById(oid) : null;
    var entityType = _itEntityTypeForOrder(order);
    var entityId = String(oid);
    var key = entityType + ':' + entityId;
    (byKey[key] = byKey[key] || []).push(card);
    if (!seen[key]) { seen[key] = true; pairs.push([entityType, entityId]); }
  });
  if (!pairs.length) return;

  // Auth token (jak reszta modułów: SellBaseAPI.getAuthToken() → localStorage)
  var w: any = window;
  var token = (w.SellBaseAPI && w.SellBaseAPI.getAuthToken && w.SellBaseAPI.getAuthToken())
    || localStorage.getItem('sellbase_token') || '';

  // 2) Batchuj ids (max ~200 na request) i woła POST /api/internal-tasks/lookup.
  var BATCH = 200;
  var map: Record<string, { open_count: number; assignees: string[] }> = {};
  var pewne: Record<string, boolean> = {};  // klucze z UDANYCH odpowiedzi — tylko dla nich wolno zdjąć chip
  for (var i = 0; i < pairs.length; i += BATCH) {
    var chunk = pairs.slice(i, i + BATCH);
    try {
      var res = await fetch('/api/internal-tasks/lookup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
        },
        body: JSON.stringify({ pairs: chunk }),
      });
      if (!res.ok) continue;
      var part = await res.json();
      if (part && typeof part === 'object') {
        Object.keys(part).forEach(function(k) { map[k] = part[k]; });
        chunk.forEach(function(pr) { pewne[pr[0] + ':' + pr[1]] = true; });
      }
    } catch (_: any) { /* niekrytyczne — brak/blok nie psuje listy */ }
  }

  // 3) Wstrzyknij/zdejmij/podmień chip wg AKTUALNEGO open_count z odpowiedzi.
  // S-139 przegląd: ta funkcja odpytuje WSZYSTKIE widoczne karty przy KAŻDYM renderze
  // (wywołanie :427), ale karta zachowana przez morfowanie nie czyści się sama — bez
  // zdejmowania tutaj chip pracownika wisiałby na karcie do zmiany jej treści (np. do
  // zamknięcia zadania, które akurat nie zmienia HTML-a karty).
  if (pokolenie !== _itDekoratorPokolenie) return;
  Object.keys(byKey).forEach(function(key) {
    var info = map[key];
    var cardsForKey = byKey[key] || [];
    if (!pewne[key]) return;  // błąd sieci/HTTP dla tej porcji — chip zostaje, jak był
    if (!info || !info.open_count || info.open_count <= 0) {
      cardsForKey.forEach(function(card) {
        try {
          var stary = card.querySelector('.oc-it-assignee-chip');
          if (stary && stary.parentNode) stary.parentNode.removeChild(stary);
        } catch (_: any) { /* niekrytyczne */ }
      });
      return;
    }
    var assignees = Array.isArray(info.assignees) ? info.assignees : [];
    var first = assignees[0] || 'pracownik';
    var extra = info.open_count > 1 ? ' +' + (info.open_count - 1) : '';
    var titleAttr = assignees.length ? assignees.join(', ') : first;
    var tekstChipa = '👤 ' + first + extra;
    cardsForKey.forEach(function(card) {
      try {
        var istniejacy = card.querySelector('.oc-it-assignee-chip') as HTMLElement | null;
        if (istniejacy) {
          // Zadanie się zmieniło (inny pracownik/liczba) — podmień treść na zachowanej karcie.
          istniejacy.title = titleAttr;
          istniejacy.textContent = tekstChipa;
          return;
        }
        var head = card.querySelector('.order-card-head');
        if (!head) return;
        var chip = document.createElement('span');
        chip.className = 'oc-it-assignee-chip';
        chip.title = titleAttr;
        chip.style.cssText = 'display:inline-flex;align-items:center;gap:2px;background:var(--color-primary);color:#fff;font-size:10px;line-height:1;border-radius:10px;padding:2px 7px;font-weight:600;white-space:nowrap';
        chip.textContent = tekstChipa;
        // Wstaw w nagłówku karty, przed prawym blokiem (status/płatność/dostawa).
        var right = head.querySelector('.order-card-head-right');
        if (right) head.insertBefore(chip, right);
        else head.appendChild(chip);
      } catch (_: any) { /* niekrytyczne */ }
    });
  });
}

export function _buildOrderCardHTML(order: any) {
    // ── KAFELEK 2.0 (spec design_handoff 2026-08-24, klasy zk-*) ──
    // Warstwa wizualna nowa; CAŁA logika (status dropdown, locki pakowania,
    // przypisywanie produktów, system braków, tagi/produkcja) zostaje 1:1 —
    // stąd legacy klasy JS-owe (order-card-head, oc-status-btn-wrap, btn-pack,
    // assign-product-btn, oc-sb-product-link, oc-sb-location-add) obok zk-*.
    var isChecked = (state as any).ordersSelectedIds.has(order.id);
    var sourceObj = order.source || { key: 'manual', label: 'Ręczne', color: 'source-manual' };
    // 2026-06-10: order.status bywa STRINGIEM (rekord, ktory nie przeszedl przez
    // normalizeBackendOrder — np. dociagniety inna sciezka do SellBaseData.orders).
    // Bez guardu statusObj.label=undefined → kafelek statusu renderowal sie PUSTY
    // (sama strzalka). Drawer ma identyczny guard (order-drawer-core.ts).
    var statusObj = (order.status && typeof order.status === 'object')
      ? order.status
      : (order.status
          ? { key: String(order.status).toLowerCase().replace(/\s+/g, '_'), label: String(order.status), color: statusColorForLabel(String(order.status)) }
          : { key: 'new', label: 'Nowe', color: 'info' });

    // Data złożenia zamówienia z godziną
    var dateWithTime = '';
    if (order.date) {
      try {
        var _dt = new Date(order.date);
        if (!isNaN(_dt.getTime())) {
          dateWithTime = _dt.toLocaleDateString('pl-PL', {day:'2-digit',month:'2-digit',year:'numeric'}) + ' ' + _dt.toLocaleTimeString('pl-PL', {hour:'2-digit',minute:'2-digit'});
        }
      } catch (e: any) {}
      if (!dateWithTime) dateWithTime = order.dateFormatted || '';
    }

    // Tags — chipy tagów + grupa robocza + produkcja (wspólne moduły, bez zmian)
    var _otagi = (window as any).__sbOrderTags;
    var tagsWrap = _otagi ? _otagi.chipyTagow(order.tags) : '';
    // 2026-09-01: ZNACZNIK SYSTEMOWY czarnej listy — wyglada jak tag, bez X.
    if (_otagi && _otagi.chipCzarnejListy) tagsWrap += _otagi.chipCzarnejListy(order);
    var _prodHead: any = (order as any).production || null;
    var _grq = (window as any).__sbGrupyRobocze;
    if (_grq && _grq.chipHtml) {
      tagsWrap += _grq.chipHtml(order);
    } else if (_prodHead && _prodHead.grupa && _prodHead.grupa.name) {
      var _gk = String(_prodHead.grupa.color || '#6b7280');
      tagsWrap += '<span class="order-card-tags"><span class="ptag" style="border-color:' + escapeAttr(_gk) +
        ';color:' + escapeAttr(_gk) + ';font-weight:700" title="Grupa robocza zamówienia">✋ ' +
        escapeHtml(String(_prodHead.grupa.name)) + '</span></span>';
    }
    if (_otagi && _otagi.chipProdukcji && _prodHead) {
      tagsWrap += _otagi.chipProdukcji(_prodHead, order);
    }
    tagsWrap += chipZlecenHtml((order as any).zlecenia);
    var noteHtml = order.notes ? '<span class="order-card-note-icon" title="' + escapeAttr(order.notes) + '">📝</span>' : '';

    // Status dropdown (prawy róg HEAD)
    var statusOptsHtml = orderStatusOptionsHtml(statusObj.label || '');
    var _statusColorMap: Record<string, any> = { 'info':'#3b82f6','warning':'#f59e0b','primary':'#6366f1','success':'#22c55e','error':'#ef4444' };
    var _statusColor = _statusColorMap[statusObj.color || 'info'] || '#6b7280';

    var items = Array.isArray(order.items) ? order.items : [];

    // Review serii: href TYLKO http(s) — shop_url PS pochodzi z konfigury
    // usera (stored-XSS hardening, domowy wzorzec safeHttpUrl).
    var safeHttp = function(u: any): string {
      var raw = String(u || '').trim();
      if (!raw) return '';
      try { var p = new URL(raw, window.location.origin);
        return (p.protocol === 'http:' || p.protocol === 'https:') ? p.href : ''; }
      catch (_e) { return ''; }
    };

    // 2026-08-25 (user): „SKU ma być najbardziej wyszczególnione (…) po kliknięciu
    // w SKU nie otwiera drawera tylko kopiuje je do schowka". Etykieta zostaje
    // szara i mała, wartość idzie na pełny kontrast; EAN pośrodku — widoczny,
    // ale nie konkuruje z SKU. Kopiowanie obsługuje delegat w orders-shared.ts
    // (karty przerysowują się w kółko, listener na elemencie by nie przeżył).
    // 2026-09-03 (user): `diff` = kod różni się między ofertą marketplace a produktem
    // SellBase → wartość na czerwono + tooltip z oboma kodami (roznicaKodow/roznyKod).
    var _kodWart = function(kod: string, diff?: boolean) {
      return diff ? '<span class="zk-code--diff" style="' + KOD_DIFF_STYLE + '">' + escapeHtml(kod) + '</span>' : escapeHtml(kod);
    };
    var _kodSku = function(sku: string, diff?: boolean, tytul?: string) {
      return '<span class="zk-code zk-code--sku oc-sku-copy" data-sku="' + escapeAttr(sku) + '"'
        + ' title="' + escapeAttr(diff && tytul ? tytul + ' — kliknij, aby skopiować' : 'Kliknij, aby skopiować SKU') + '" onclick="event.stopPropagation()">'
        + '<span class="zk-code-lab">SKU</span>' + _kodWart(sku, diff) + '</span>';
    };
    var _kodEan = function(ean: string, diff?: boolean, tytul?: string) {
      return '<span class="zk-code zk-code--ean"' + (diff && tytul ? ' title="' + escapeAttr(tytul) + '"' : '') + '><span class="zk-code-lab">EAN</span>'
        + _kodWart(ean, diff) + '</span>';
    };
    // 2026-10-02 (user, #118185): sygnatura oferty Allegro (offer.external.id) jako osobny kod — wygląd EAN-u,
    // klik kopiuje (dotąd szła jako „SKU" i dało się ją skopiować; delegat w orders-shared.ts, data-kod-nazwa → toast).
    var _kodSygnatura = function(sygn: string, diff?: boolean, tytul?: string, zakup?: string) {
      var tyt = (diff && tytul ? tytul : 'Sygnatura oferty Allegro') + (zakup ? '. ' + zakup : '') + ' — kliknij, aby skopiować';
      return '<span class="zk-code zk-code--ean zk-code--sygn oc-sku-copy" data-sku="' + escapeAttr(sygn) + '" data-kod-nazwa="sygnaturę"'
        + ' title="' + escapeAttr(tyt) + '" onclick="event.stopPropagation()" style="cursor:pointer">'
        + '<span class="zk-code-lab">SYGNATURA</span>' + _kodWart(sygn, diff) + '</span>';
    };

    // ── Kolumna 1: OFERTY ────────────────────────────────────────
    var _reczneKarta = !!(order.is_manual === true || order.is_manual === 1 ||
      String((order.source && order.source.key) || '').toLowerCase() === 'manual');
    var offersHtml = items.map(function(it: any) {
      // 2026-08-26 (zgłoszenie usera, zamówienie HUF): formatCurrency bez drugiego
      // argumentu domyśla się PLN — pozycja za 1260 HUF pokazywała „1260,00 PLN"
      // obok poprawnych plakietek HUF liczonych z order.currency.
      var priceStr = it.unit_price ? SellBaseData.formatCurrency(it.unit_price, it.currency || order.currency) : '';
      var qtyBadge = '<span class="zk-offer-qty">×' + (parseInt(String(it.quantity || 1), 10) || 1) + '</span>';
      var _safeOfferUrl = safeHttp(it.offer_url);
      // 2026-08-25 (user): „jeżeli to zamówienie jest z Allegro to obok każdej
      // pozycji ma być button allegro, który otwiera ofertę w nowej karcie".
      // `offer_url` w zapisanych pozycjach jest PUSTE (sprawdzone na produkcji:
      // items_json nie ma tego pola dla żadnego marketplace'u), więc adres
      // budujemy z offer_id. Domena zawsze .pl — oferta sprzedawcy ma bazowy
      // marketplace allegro-pl, a zagraniczne to publikacje dodatkowe
      // (GET /sale/product-offers/{id} → publication.marketplaces.base).
      if (!_safeOfferUrl && order.is_allegro && it.offer_id) {
        _safeOfferUrl = 'https://allegro.pl/oferta/' + encodeURIComponent(String(it.offer_id));
      }
      var extLink = '';
      if (_safeOfferUrl && order.is_allegro) {
        extLink = '<a class="zk-offer-allegro" href="' + escapeAttr(_safeOfferUrl) + '" target="_blank" rel="noopener"'
          + ' onclick="event.stopPropagation()" title="Otwórz ofertę na Allegro">'
          + '<span class="zk-allegro-dot"></span>Allegro'
          + '<i data-lucide="external-link" style="width:10px;height:10px"></i></a>';
      } else if (_safeOfferUrl) {
        extLink = '<a href="' + escapeAttr(_safeOfferUrl) + '" target="_blank" rel="noopener" onclick="event.stopPropagation()"'
          + ' title="Otwórz ofertę na marketplace" style="display:inline-flex;flex-shrink:0;color:var(--pk-t4)">'
          + '<i data-lucide="external-link" style="width:12px;height:12px"></i></a>';
      }
      var thumbSrc = String(it.offer_image_url || it.image_url || it.sellbase_image_url || '');
      var thumb = thumbSrc
        ? '<img class="oc-offer-thumb sb-zoom" src="' + escapeAttr(thumbSrc) + '" loading="lazy"'
          + ' onerror="this.style.display=\'none\'"'
          + ' style="width:26px;height:26px;object-fit:cover;border-radius:4px;background:#fff;flex-shrink:0">'
        : '';
      var mpSku = String(it.sku || it.product_reference || it.seller_sku || '');
      var mpEan = String(it.ean || '');
      var _rk = roznicaKodow(it);
      var subParts = [];
      if (order.is_allegro) {
        // 2026-10-02 (user, #118185): „przy pozycji Allegro EAN, SKU, SYGNATURĘ ALLEGRO" — w tej kolejności.
        // SKU = produkt SellBase (Allegro nie ma pola SKU; `it.sku` to sygnatura), patrz kodyPozycjiAllegro.
        var _ka = kodyPozycjiAllegro(it);
        if (_ka.ean) subParts.push(_kodEan(_ka.ean, _ka.eanDiff, kodDiffTitle('EAN', _ka.ean, _ka.prodEan)));
        _ka.skuLista.forEach(function(sku: string) { subParts.push(_kodSku(sku)); }); // zestaw: każde SKU osobno (klik kopiuje jedno)
        if (_ka.sygnatura) subParts.push(_kodSygnatura(_ka.sygnatura, _ka.sygnaturaDiff, sygnaturaDiffTitle(_ka.sygnatura, _ka.prodSku), sygnaturaZakupuTitle(_ka.sygnaturaZakupu)));
      } else {
        if (mpSku) subParts.push(_kodSku(mpSku, _rk.sku, kodDiffTitle('SKU', mpSku, _rk.prodSku)));
        if (mpEan) subParts.push(_kodEan(mpEan, _rk.ean, kodDiffTitle('EAN', mpEan, _rk.prodEan)));
      }
      // Allegro: trzy kody — zawijamy całe kody do nowej linii zamiast ucinać sygnaturę „…".
      var subline = subParts.length
        ? '<div class="zk-offer-codes" style="' + (order.is_allegro ? 'white-space:normal' : 'overflow:hidden;text-overflow:ellipsis') + '">' + subParts.join('<span class="zk-code-sep">·</span>') + '</div>'
        : '';
      var titleHtml = '';
      if (order.is_allegro && it.offer_id) {
        var _accId = order.allegroAccountId || 0;
        titleHtml = '<span class="zk-offer-title" data-offer-id="' + escapeAttr(it.offer_id) + '" data-account-id="' + _accId + '" title="' + escapeAttr(it.name || '') + '" onclick="event.stopPropagation();SellBaseApp.openOfferModal(\'' + escapeAttr(it.offer_id) + '\',' + _accId + ')" style="display:block">' +
          zestawAllegroChip((it as any).zestaw_allegro) + escapeHtml(it.name) +  // S-57: „Wielopak/Zestaw Allegro” przed tytułem
        '</span>';
      } else if (order.is_prestashop && it.product_id) {
        // PS: klik w tytuł → modal z danymi produktu PS
        var _psAccId = parseInt(String(order.id || '').split(':')[0]) || 0;
        titleHtml = '<span class="zk-offer-title" data-ps-product-id="' + escapeAttr(it.product_id) + '" data-account-id="' + _psAccId + '" title="' + escapeAttr(it.name || '') + '" onclick="event.stopPropagation();SellBaseApp.openPSProductModal(' + it.product_id + ',' + _psAccId + ',' + escapeAttr(JSON.stringify({name:it.name||'',sku:it.sku||'',ean:it.ean||'',unit_price:it.unit_price||0})) + ')" style="display:block">' +
          escapeHtml(it.name) +
        '</span>';
      } else {
        titleHtml = '<span class="zk-offer-title" title="' + escapeAttr(it.name || '') + '" style="display:block;cursor:default">' + escapeHtml(it.name) + '</span>';
      }
      var titleCol = '<div style="flex:1;min-width:0">' + titleHtml + subline + '</div>';
      var _allegroNaKoncu = (order.is_allegro && extLink) ? extLink : '';
      var _linkPrzedIloscia = _allegroNaKoncu ? '' : extLink;
      return '<div class="zk-offer-line">' + _linkPrzedIloscia + qtyBadge + thumb + titleCol
        + (priceStr ? '<span class="zk-offer-price">' + priceStr + '</span>' : '')
        + _allegroNaKoncu + '</div>';
    }).join('');

    // ── PRODUKTY SELLBASE — mapa znaczników produkcji (bez zmian) ─
    var _prodZnaczniki: any = null;
    var _produkcja: any = (order as any).production || null;
    if (_produkcja && Array.isArray(_produkcja.pozycje)) {
      _prodZnaczniki = {};
      var _kolorGrupy = (_produkcja.grupa && _produkcja.grupa.color) || '';
      // Duplikaty SKU (ponowne zgłoszenie tej samej pozycji): wygrywa OTWARTA
      // nad domkniętą, wśród otwartych bardziej zaawansowany stan, wśród
      // domkniętych późniejsza — nie „losowy ostatni wiersz zapytania".
      var _ranga: any = { assembling: 50, printing: 40, failed: 35, printed: 30, queued: 20, reported: 10 };
      _produkcja.pozycje.forEach(function(poz: any) {
        if (!poz || !poz.sku) return;
        var k = String(poz.sku);
        var stary: any = _prodZnaczniki[k];
        if (stary) {
          var starOtw = !stary.done, nowOtw = !poz.done;
          if (starOtw && !nowOtw) return;                       // otwarta zostaje
          if (starOtw === nowOtw) {
            if (nowOtw) {
              var rN = _ranga[poz.status] || 0, rS = _ranga[stary.status] || 0;
              if (rN < rS) return;
              // Remis rangi (typowo dwa 'reported' po ponownym zgłoszeniu):
              // deterministycznie wygrywa NOWSZE since — kolejność wierszy
              // SELECT-a jest fizyczna (heap), nie chronologiczna.
              if (rN === rS && String(poz.since || '') <= String(stary.since || '')) return;
            } else if (String(poz.since || '') <= String(stary.since || '')) return;
          }
        }
        _prodZnaczniki[k] = {
          status: poz.status, worker: poz.worker, eta: poz.eta,
          printer: poz.printer, note: poz.note, progress: poz.progress,   // 06.10.2026: drukarka/notatka/postęp panelu
          since: poz.since, done: !!poz.done, groupColor: _kolorGrupy,
          reason_label: poz.reason_label, reason_emoji: poz.reason_emoji,
          orderId: order.id, sku: k,
          source: (order.source && order.source.key) || '',
        };
      });
    }

    // Karta produktu (decyzja usera 2026-08-24 pkt 4): [wykrzyknik braku 34×34]
    // [lokalizacja amber h34][×N zielona ramka h34][ZIELONY NAWIAS][miniatura]
    // [nazwa UPPERCASE + SKU·EAN] — i nic więcej. Znacznik produkcji zostaje
    // po nazwie (funkcja, nie dekoracja — wcześniejsza twarda decyzja usera).
    var prodCardsHtml = items.map(function(it: any) {
      var prods = Array.isArray(it.sellbase_products) ? it.sellbase_products : [];
      if (!prods.length && it.sellbase_product_id) {
        prods = [{ id: it.sellbase_product_id, name: it.sellbase_product_name || '', sku: it.sellbase_sku || '',
          ean: it.sellbase_ean || '', image_url: it.sellbase_image_url || '', location: it.sellbase_location || '',
          link_quantity: it.sellbase_link_quantity || 1 }];
      }
      var orderQty = parseInt(String(it.quantity || 1), 10) || 1;
      if (!prods.length) {
        // Pozycja bez powiązania — amber lewa krawędź + Przypisz (bind niżej w pliku).
        return '<div class="zk-prod-row">' +
          '<div class="zk-prod-card zk-prod-card--unlinked">' +
            '<div style="flex:1;min-width:0">' +
              '<div class="zk-prod-name">' + escapeHtml(String(it.name || '')) + '</div>' +
              '<div class="zk-prod-codes">Brak powiązanego produktu SellBase</div>' +
            '</div>' +
            '<button class="assign-product-btn zk-assign-btn" data-item-id="' + escapeAttr(String(it.id || it.line_item_id || '')) + '" onclick="event.stopPropagation()">Przypisz produkt</button>' +
          '</div>' +
        '</div>';
      }
      return prods.map(function(p: any) {
        var linkQty = parseInt(String(p.link_quantity || 1), 10) || 1;
        var totalQty = orderQty * linkQty;
        var znacznik = (_prodZnaczniki && p.sku) ? _prodZnaczniki[String(p.sku)] : null;
        // Klasa oc-sb-nostock-btn OBOWIĄZKOWA — delegat systemu braków
        // (shortage-report.ts:392) łapie klik przez closest('.oc-sb-nostock-btn').
        var shortageBtn = '<button class="zk-prod-shortage oc-sb-nostock-btn" onclick="event.stopPropagation()" title="Zgłoś brak / do druku"' +
          ' data-shortage-product-id="' + escapeAttr(String(p.id || '')) + '"' +
          // 2026-08-27 (zgłoszenie usera, 56AFA200): produkt bez SKU → zgłoszenie
          // szło bez SKU (Telegram bez kodu, panel odrzucał). SKU oferty
          // z zamówienia jest fallbackiem TYLKO dla zgłoszenia (kafelek dalej
          // pokazuje kody magazynowe osobno od ofertowych).
          ' data-shortage-sku="' + escapeAttr(p.sku || it.sku || '') + '"' +
          ' data-shortage-name="' + escapeAttr(p.name || '') + '"' +
          ' data-shortage-qty="' + totalQty + '"' +
          ' data-shortage-img="' + escapeAttr(p.image_url || '') + '">' +
          '<i data-lucide="alert-circle" style="width:16px;height:16px"></i></button>';
        // 2026-08-26 (user, kolejność kafelka): lokalizacja siedzi ZA TYTUŁEM
        // (w kafelku, ta sama linia co nazwa) i jest wyrazista — pełny amber.
        // 21.10.2026 (właściciel: „na liście zamówień to nie działa”): ten sam edytowalny znaczek co w karcie
        // zamówienia (orders-shared.lokalizacjaBadgeHtml), w wyglądzie kafelka listy — klik = pole w miejscu.
        var locBadge = lokalizacjaBadgeHtml(p.id, p.location, 'zk');
        var pidAttr = p.id ? ' data-product-id="' + escapeAttr(String(p.id)) + '"' : '';
        var clickStyle = p.id ? ';cursor:pointer' : '';
        var thumb = p.image_url
          ? '<img class="zk-prod-thumb sb-zoom oc-sb-product-link"' + pidAttr + ' src="' + escapeAttr(p.image_url) + '" alt="" loading="lazy" title="Otwórz produkt" style="' + (p.id ? 'cursor:pointer' : '') + '" onerror="this.style.visibility=\'hidden\'">'
          : '<span class="zk-prod-thumb oc-sb-product-link"' + pidAttr + ' style="display:inline-flex;align-items:center;justify-content:center' + clickStyle + '"><i data-lucide="package" style="width:14px;height:14px;opacity:.4"></i></span>';
        var codes = [];
        // zestaw (kilka produktów SB) ma inne kody z natury — porównanie tylko dla jednego produktu
        var _skuDiff = prods.length === 1 && roznyKod(it.sku, p.sku);
        var _eanDiff = prods.length === 1 && roznyKod(it.ean, p.ean);
        if (p.sku) codes.push(_kodSku(String(p.sku), _skuDiff, kodDiffTitle('SKU', it.sku, p.sku)));
        if (p.ean) codes.push(_kodEan(String(p.ean), _eanDiff, kodDiffTitle('EAN', it.ean, p.ean)));
        var znHtml = znacznik ? ' ' + _prodZnacznikHtml(znacznik) : '';
        var borderStyle = (znacznik && znacznik.groupColor) ? ' style="border-color:' + escapeAttr(String(znacznik.groupColor)) + '"' : '';
        // 2026-08-24 (user, seria #2): akcje [brak][lokalizacja][xN] POZA
        // kafelkiem — zielona krawędź otwiera dopiero kafelek miniaturki
        // (struktura jak w starym oc-sb-row).
        // 2026-08-26 (user): kolejność w wierszu — ILOŚĆ, MINIATURKA (większa),
        // TYTUŁ, za tytułem LOKALIZACJA, na końcu ikona zgłaszania braku.
        return '<div class="zk-prod-row">' +
          '<span class="zk-prod-qty">×' + totalQty + '</span>' +
          '<div class="zk-prod-card"' + borderStyle + '>' +
            thumb +
            '<div style="flex:1;min-width:0">' +
              '<div style="display:flex;align-items:center;gap:8px;min-width:0">' +
                '<div class="zk-prod-name oc-sb-product-link"' + pidAttr + ' title="' + escapeAttr(String(p.name || '')) + '" style="' + (p.id ? 'cursor:pointer' : '') + '">' + escapeHtml(String(p.name || '')) + znHtml + '</div>' +
                locBadge +
              '</div>' +
              (codes.length ? '<div class="zk-prod-codes">' + codes.join('<span class="zk-code-sep">·</span>') + '</div>' : '') +
            '</div>' +
          '</div>' +
          shortageBtn +
        '</div>';
      }).join('');
    }).join('');

    // ── HEAD: plakietka marketplace + konto (jednolity rozmiar zk-badge) ─
    var sourceBadgeHtml;
    if (sourceObj.key === 'allegro') {
      var accLabel = order.allegroAccountName ? escapeHtml(order.allegroAccountName) : '';
      sourceBadgeHtml = '<span class="zk-badge zk-badge--mp"><span class="oc-source-allegro-dot"></span>Allegro' +
        (accLabel ? '<span style="opacity:.85">· ' + accLabel + '</span>' : '') + '</span>';
    } else {
      sourceBadgeHtml = '<span class="zk-badge">' + escapeHtml(sourceObj.label || '') + '</span>';
    }
    var _mpCountryMap = {'allegro-hu':{code:'hu',name:'Węgry'},'allegro-cz':{code:'cz',name:'Czechy'},'allegro-sk':{code:'sk',name:'Słowacja'}};
    // Faza 77: konta Business dostają badge "BUSINESS" (fioletowy) zamiast flagi.
    var _mpId = String(order.marketplace_id || '');
    var _isBusiness = _mpId === 'allegro-business-pl' || _mpId === 'allegro-business-cz';
    if (_isBusiness) {
      sourceBadgeHtml += ' <span style="font-size:9px;padding:2px 6px;border-radius:4px;background:rgba(139,92,246,.15);color:var(--color-violet-text);font-weight:700;letter-spacing:.05em;display:inline-block;vertical-align:middle;border:1px solid rgba(139,92,246,.3)" title="Allegro Business">BUSINESS</span>';
    } else {
      var _mpInfo = (_mpCountryMap as any)[_mpId];
      if (_mpInfo) {
        sourceBadgeHtml += ' <img src="https://flagcdn.com/16x12/' + _mpInfo.code + '.png" alt="' + escapeAttr(_mpInfo.name) + '" title="' + escapeAttr(_mpInfo.name) + '" style="width:18px;height:13px;border-radius:2px;display:inline-block;vertical-align:middle;background:transparent">';
      }
    }

    // ── Kolumna 2: KUPUJĄCY ──────────────────────────────────────
    var customerName = order.customer || '';
    var buyerPhone = order.customerPhone || order.phone || '';
    var buyerEmail = order.customerEmail || order.email || '';
    var buyerMsg = String(order.message_to_seller || order.buyer_message || '').trim();
    var _note = order.internal_note || null;
    var _noteText = _note ? String(_note.text || '').trim() : '';
    var internalNoteHtml = '';
    if (_noteText) {
      var _noteAuthor = String(_note.author || '').trim();
      var _noteExtra = (_note.count && _note.count > 1) ? (' +' + (_note.count - 1)) : '';
      internalNoteHtml = '<div class="oc-note-bar" style="margin-top:8px">' +
        '<div class="oc-note-row" data-note-kind="internal" title="' + escapeAttr(_noteText) + '">' +
          '<i data-lucide="sticky-note" class="oc-note-ico"></i>' +
          '<span class="oc-note-label">Notatka:</span>' +
          '<span class="oc-note-text">' + escapeHtml(_noteText) + '</span>' +
          (_noteAuthor ? '<span class="oc-note-author">' + escapeHtml(_noteAuthor) + escapeHtml(_noteExtra) + '</span>' : '') +
        '</div>' +
      '</div>';
    }

    // 2026-08-27 (prośba usera): kto wysłał — dyskretnie obok badge'y.
    // Dane z enrichu listingu (order_fulfillment_actors); brak atrybucji
    // (auto-etykiety workera, wysyłka poza SellBase) = brak dopisku.
    function _buildShippedByHtml(o: any): string {
      var kto = String((o && o.shipped_by) || '').trim();
      if (!kto) return '';
      var kiedy = String((o && o.shipped_by_at) || '');
      var kiedyTxt = kiedy ? formatLocalDateTime(kiedy) : '';  // 2026-09-08: czas lokalny, nie surowe UTC
      // v2 (2026-08-27, user: „bardziej wyszczególnione, na czerwono, żeby od
      // razu było widać"): czerwona pigułka zamiast szarego dopisku.
      return ' <span class="zk-shipped-by" title="' + escapeAttr('Wysłał: ' + kto + (kiedyTxt ? ' · ' + kiedyTxt : '')) + '"' +
        ' style="font-size:10px;font-weight:700;color:#dc2626;background:rgba(220,38,38,.08);' +
        'border:1px solid rgba(220,38,38,.35);border-radius:9999px;padding:2px 8px;' +
        'white-space:nowrap;display:inline-flex;align-items:center;gap:4px">' +
        '<i data-lucide="send" style="width:10px;height:10px"></i>Wysłał: ' + escapeHtml(kto) + '</span>';
    }

    // ── Kolumna 3: DOSTAWA ───────────────────────────────────────
    var addr = order.shippingAddress || {};
    var deliveryMethod = order.delivery ? (order.delivery.method || '') : '';
    var deliveryAddrParts = [];
    if (addr.street) deliveryAddrParts.push(addr.street);
    var deliveryPostCity = [(addr.postal_code || ''), (addr.city || '')].filter(Boolean).join(' ');
    if (deliveryPostCity) deliveryAddrParts.push(deliveryPostCity);
    var pickup = order.delivery && order.delivery.pickup_point;
    if (pickup && pickup.id) deliveryAddrParts.push('📦 ' + String(pickup.id));

    // Kwoty (spec pkt 6): suma OFERT i koszt dostawy = TEN SAM chip;
    // zielona suma ZAMÓWIENIA = oferty + dostawa (SMART liczy się jako 0).
    var _delCost = order.deliveryCostAmount || 0;
    var deliveryChip = _delCost > 0
      ? '<span class="zk-money-chip">' + SellBaseData.formatCurrency(_delCost, order.currency) + '</span>'
      : (order.is_allegro
          ? '<span class="zk-money-chip zk-money-chip--smart">SMART</span>'
          : '<span class="zk-money-chip zk-money-chip--free">' + SellBaseData.formatCurrency(0, order.currency) + '</span>');

    // ── Listwa PAKUJ (logika PACKABLE/locki bez zmian; klasy btn-pack
    //    zostają — packing.ts:_updatePackingButtons podmienia po nich stan) ─
    var railInner = (function() {
      var ordId = order.id;
      // ETAP 3 (2026-08-10): lista z KLUCZY; slugi polskich nazw jako zapas
      // dla danych sprzed etapu 3 (pełna historia w git blame).
      var statusKey = String((order.status && order.status.key) || '').toLowerCase().replace(/\s+/g, '_');
      var PACKABLE = [
        '', 'new', 'nowe',
        'in_progress', 'processing', 'w_realizacji',
        'packed', 'spakowane',
        'ready_to_ship', 'do_wyslania',
      ];
      if (PACKABLE.indexOf(statusKey) === -1) {
        return ''; // status po pakowaniu — bez przycisku
      }
      var lock = (state as any).packingLocks[ordId];
      if (lock && !lock.is_mine) {
        return '<div class="oc-packing-row">' +
          '<button class="zk-pack-btn btn-pack btn-pack-locked" disabled title="' + escapeAttr(lock.user_name) + ' pakuje to zamówienie">' +
            '<i data-lucide="lock" style="width:18px;height:18px"></i>' + escapeHtml(lock.user_name) +
          '</button></div>';
      }
      if ((state as any).packingOrderId === ordId) {
        return '<div class="oc-packing-row">' +
          '<button class="zk-pack-btn btn-pack btn-pack-cancel" onclick="event.stopPropagation();SellBaseApp.togglePacking(\'' + escapeAttr(ordId) + '\')">' +
            '<i data-lucide="x" style="width:18px;height:18px"></i>ANULUJ' +
          '</button></div>';
      }
      return '<div class="oc-packing-row">' +
        '<button class="zk-pack-btn btn-pack btn-pack-start" onclick="event.stopPropagation();SellBaseApp.togglePacking(\'' + escapeAttr(ordId) + '\')">' +
          '<i data-lucide="package" style="width:20px;height:20px"></i>PAKUJ' +
        '</button></div>';
    })();

    // 2026-09-04 (polecenie właściciela): na karcie widać ID SellBase (public_id, ULID); identyfikator
    // marketplace zostaje w podpowiedzi i w drawerze (osobna linia „ID <źródło>").
    var marketplaceId = String(order.allegro_id || order.source_order_id || String(order.id || '').replace(/^AL-/i, '') || '');
    var _pidKarta = String(order.public_id || '').trim().toUpperCase();
    var _nrKarta = (order.short_no != null && /^\d+$/.test(String(order.short_no))) ? String(order.short_no) : '';
    var displayId = _nrKarta || (/^[0-9A-HJKMNP-TV-Z]{26}$/.test(_pidKarta) ? _pidKarta : String(order.id || '').replace(/^AL-/i, ''));
    var _srcNazwa = (order.source && typeof order.source === 'object' && order.source.label) ? String(order.source.label) : String(order.source || 'marketplace');
    var idTooltip = 'Numer SellBase' + ((marketplaceId && marketplaceId !== displayId) ? (' · ID ' + _srcNazwa + ': ' + marketplaceId) : '') + (_nrKarta && _pidKarta ? ' · klucz: ' + _pidKarta : '');
    // Do inline-onclick: bez cudzysłowów/backslashy, żeby nie rozerwać atrybutu.
    var copyId = displayId.replace(/['"\\]/g, '');

    // 2026-09-08 (prośba właściciela): adres zamówienia na karcie — środkowy przycisk / Ctrl+klik
    // otwierają nową kartę (obsługa w bind poniżej), a numer jest prawdziwym linkiem (PPM → „otwórz w nowej karcie").
    var _adresKarty = orderUrl(order);
    return '<div class="order-card zk" data-order-id="' + escapeAttr(order.id) + '" data-order-url="' + escapeAttr(_adresKarty) + '">' +
      orderSearchGroupContextHtml(order) +

      // HEAD: [☐] [data] [marketplace+konto] [#ID+kopiuj] [tagi] → [status▼ · płatność · przesyłka]
      '<div class="zk-head order-card-head">' +
        '<input type="checkbox" class="order-card-check"' + (isChecked ? ' checked' : '') + ' aria-label="Zaznacz zamówienie" onclick="event.stopPropagation()">' +
        (dateWithTime ? '<span class="zk-head-date">' + escapeHtml(dateWithTime) + '</span>' : '') +
        sourceBadgeHtml +
        '<a class="zk-head-id" href="' + escapeAttr(_adresKarty) + '" title="' + escapeAttr(idTooltip) + '">#' + escapeHtml(displayId) + '</a>' +
        '<button class="zk-copy-btn" title="Kopiuj numer zamówienia" onclick="event.stopPropagation();try{navigator.clipboard.writeText(\'' + escapeAttr(copyId) + '\');window.showToast&&window.showToast(\'Skopiowano numer\',\'success\')}catch(e){}">' +
          '<i data-lucide="copy" style="width:12px;height:12px"></i></button>' +
        tagsWrap + noteHtml +
        '<span class="zk-head-right order-card-head-right">' +
          '<div class="oc-status-btn-wrap" style="position:relative" onclick="event.stopPropagation()">' +
            '<button class="oc-status-btn" data-order-id="' + escapeAttr(order.id) + '" style="display:inline-flex;align-items:center;gap:3px;padding:2px 8px;border-radius:9999px;font-size:10px;font-weight:600;background:' + _statusColor + '18;color:' + _statusColor + ';border:1px solid ' + _statusColor + '30;cursor:pointer">' +
              // ETAP 3: nazwa nadana przez firmę bije tłumaczenie (patrz `etykietaStatusu`).
              '<span data-status-cat="order" data-status-key="' + escapeAttr(statusObj.key || '') + '">' + escapeHtml(((window as any).__sbExports && (window as any).__sbExports.etykietaStatusu) ? (window as any).__sbExports.etykietaStatusu(statusObj.key, statusObj.label || '') : ((window.__sbI18n && window.__sbI18n.tStatus) ? window.__sbI18n.tStatus('order', statusObj.key, statusObj.label || '') : (statusObj.label || ''))) + '</span>' +
              '<i data-lucide="chevron-down" style="width:10px;height:10px;vertical-align:middle"></i>' +
            '</button>' +
            '<div class="oc-status-dropdown" style="display:none">' +
              '<select class="order-card-status-sel" style="display:none" aria-label="Zmień status">' + statusOptsHtml + '</select>' +
            '</div>' +
          '</div>' +
          _buildPaymentBadge(order) + ' ' + _buildShipmentBadge(order) + _buildShippedByHtml(order) +
        '</span>' +
      '</div>' +

      '<div class="zk-grid">' +

        // KOLUMNA 1: pozycje + karty produktów SellBase (bez nagłówka „Oferty"
        // i chipa sumy — user 2026-08-25) + notatka wewnętrzna pod pozycjami
        // (2026-08-26, user: w wąskiej kolumnie KUPUJĄCY była ucięta).
        '<div class="zk-col">' +
          // 2026-09-02 (user): zamówienie RĘCZNE — pozycja jest produktem z katalogu SellBase,
          // wiersz „oferty" dublował kafelek produktu (a ten pokazywał ×90000 przez krotność).
          (_reczneKarta ? '' : offersHtml) +
          prodCardsHtml +
          internalNoteHtml +
        '</div>' +

        // KOLUMNA 2: KUPUJĄCY + wiadomość od kupującego
        '<div class="zk-col">' +
          '<div class="zk-hdr-row"><span class="zk-hdr">Kupujący</span></div>' +
          (customerName ? '<div class="zk-buyer-name">' + escapeHtml(customerName) + '</div>' : '') +
          (buyerPhone ? '<div class="zk-buyer-line">' + escapeHtml(formatujTelefon(buyerPhone)) + '</div>' : '') +
          (buyerEmail ? '<div class="zk-buyer-line zk-buyer-line--email">' + escapeHtml(buyerEmail) + '</div>' : '') +
          (order.buyer_login ? '<div class="zk-buyer-line">' + escapeHtml(order.buyer_login) + '</div>' : '') +
          (buyerMsg
            ? '<div class="zk-msg-block"><div class="zk-hdr">Wiadomość od kupującego</div>' +
              '<div class="zk-msg-body">' + escapeHtml(buyerMsg) + '</div></div>'
            : '') +
        '</div>' +

        // KOLUMNA 3: DOSTAWA + zielona suma zamówienia
        '<div class="zk-col">' +
          '<div class="zk-hdr-row"><span class="zk-hdr">Dostawa</span>' + deliveryChip + '</div>' +
          (deliveryMethod ? '<div class="zk-carrier">' + escapeHtml(deliveryMethod) + '</div>' : '') +
          (deliveryAddrParts.length ? '<div class="zk-delivery-addr">' + deliveryAddrParts.map(escapeHtml).join('<br>') + '</div>' : '') +
          '<div class="zk-order-total-row">' +
            '<span class="zk-order-total-label">ZAMÓWIENIE</span>' +
            '<span class="zk-order-total">' + SellBaseData.formatCurrency(order.amount, order.currency) + '</span>' +
          '</div>' +
        '</div>' +

        // LISTWA PAKUJ
        '<div class="zk-rail">' + railInner + '</div>' +

      '</div>' +
    '</div>';
}

// ── EXPOSE ──
(window as any).SellBaseApp = (window as any).SellBaseApp || {};
(window as any).renderOrderCards = renderOrderCards; (window as any).SellBaseApp.renderOrderCards = renderOrderCards;
(window as any)._buildOrderCardHTML = _buildOrderCardHTML; (window as any).SellBaseApp._buildOrderCardHTML = _buildOrderCardHTML;
